import { useCallback, useMemo } from 'react';
import { PositionResource } from '../../resources';
import { GetResourceHeaderForMultiAccount, GetResourceHeaderWithToken } from '../useResource';

const validateNote = (note: string) => {
  const trimmedNote = note.trim();
  return trimmedNote === '' ? null : trimmedNote;
};

export type UseOpenPositionsNotesCommonParams = {
  getResourceHeaderWithToken: GetResourceHeaderWithToken;
  getResourceHeaderForMultiAccount: GetResourceHeaderForMultiAccount;
  positionResource: PositionResource;
};

export const useOpenPositionsNotesCommon = ({
  getResourceHeaderWithToken,
  getResourceHeaderForMultiAccount,
  positionResource,
}: UseOpenPositionsNotesCommonParams) => {
  const getNotes = useCallback(async () => {
    const res = await positionResource.getOpenPositionNotes(
      {},
      {
        ...getResourceHeaderForMultiAccount(),
      },
    );
    return res.data;
  }, [positionResource, getResourceHeaderForMultiAccount]);

  const createNote = useCallback(
    async (note: string, ric: string, accountId: string) => {
      const validatedNote = validateNote(note);
      if (validatedNote === null) {
        return;
      }

      await positionResource.createOpenPositionNotes(
        {
          ric,
          createOpenPosiontionNoteRequest: { content: validatedNote },
        },
        {
          ...getResourceHeaderWithToken(),
          accountId: accountId,
        },
      );
      await getNotes();
    },
    [positionResource, getResourceHeaderWithToken, getNotes],
  );

  const modifyNote = useCallback(
    async (note: string, ric: string, accountId: string) => {
      await positionResource.modifyOpenPositionNotes(
        {
          ric,
          modifyOpenPosiontionNoteRequest: { content: validateNote(note) },
        },
        {
          ...getResourceHeaderWithToken(),
          accountId: accountId,
        },
      );
      await getNotes();
    },
    [positionResource, getResourceHeaderWithToken, getNotes],
  );

  return useMemo(() => ({ getNotes, createNote, modifyNote }), [getNotes, createNote, modifyNote]);
};
